import { useQuery } from '@apollo/client';
import { Trans, useI18n } from '@i18n';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, IconButton, Stack, SxProps, Theme } from '@mui/material';
import {
  building_state_enum,
  class_of_use_enum,
  efficiency_class_enum,
  energy_certificate_type_enum,
  energy_source_type_enum,
  org_sector_type_enum,
  tax_bracket_enum,
  type_of_use_enum,
} from '@predium/enums';
import { Units } from '@predium/utils';
import { Fragment, useCallback, useMemo } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { BuildingFilters, BuildingFiltersKeys } from '@/components/building-filters/BuildingFilters';
import { useBuildingFilters } from '@/components/building-filters/BuildingFiltersProvider';
import BuildingSystemStatusFilter from '@/components/building-filters/Filters/BuildingSystemStatusFilter';
import CheckboxFilter, { CheckboxSearchParamsArray } from '@/components/building-filters/Filters/CheckboxFilter';
import ChipFilter, { ChipSearchParamsArray } from '@/components/building-filters/Filters/ChipFilter';
import EnergyCertificateTypeFilter from '@/components/building-filters/Filters/EnergyCertificateTypeFilter';
import { HasConsumptionDataFilter } from '@/components/building-filters/Filters/HasConsumptionDataFilter';
import RangeFilter from '@/components/building-filters/Filters/RangeFilter';
import { SlfCompliantFilter } from '@/components/building-filters/Filters/SlfCompliantFilter';
import { calculateFilterCount } from '@/components/building-filters/utils';
import Iconify from '@/components/Iconify';
import MenuPopover, { MenuPopoverArrowValue } from '@/components/MenuPopover';
import Scrollbar from '@/components/Scrollbar';
import { useSession } from '@/components/session/useSession';
import { GET_USERS_LIST_WITH_FULLNAME } from '@/graphql/Users.queries';
import { useCrremTerminology } from '@/hooks/useCrremTerminology';
import { useEuTaxonomyComplianceOptions } from '@/hooks/useEuTaxonomyComplianceOptions';
import { CrremGuard } from '@/sections/Org/Settings/util';

type Props = {
  anchorEl: HTMLElement | null;
  setAnchorEl: (value: HTMLElement | null) => void;
  isLoading?: boolean;
  hiddenFilters?: BuildingFiltersKeys[];
  responsibleUserOrgSector?: org_sector_type_enum;
  arrow?: MenuPopoverArrowValue;
  sx?: SxProps<Theme>;
};

export default function BuildingFiltersMoreMenu({
  anchorEl,
  setAnchorEl,
  isLoading,
  hiddenFilters,
  responsibleUserOrgSector,
  arrow = 'top-left',
  sx,
}: Props) {
  const { t } = useI18n();
  const { strandingYear } = useCrremTerminology();
  const {
    org: { orgSector },
  } = useSession();
  const euTaxonomyComplianceOptions = useEuTaxonomyComplianceOptions();
  const effectiveResponsibleUserOrgSector = responsibleUserOrgSector ?? orgSector;
  const { filters, applyFilters, hasValidationError, resetFilters } = useBuildingFilters();
  const { data: usersData } = useQuery(GET_USERS_LIST_WITH_FULLNAME);
  const userOptions = useMemo(
    () =>
      (usersData?.user ?? []).map((u) => ({
        id: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || String(u.id),
      })),
    [usersData?.user],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleApplyFilters = useCallback(() => {
    applyFilters();
    handleClose();
  }, [applyFilters, handleClose]);

  const handleResetFilters = useCallback(async () => {
    handleClose();
    resetFilters('more-filters-only');
  }, [handleClose, resetFilters]);

  const taxBracketOrder = useMemo(
    () => [
      tax_bracket_enum._10_90,
      tax_bracket_enum._20_80,
      tax_bracket_enum._30_70,
      tax_bracket_enum._40_60,
      tax_bracket_enum._50_50,
      tax_bracket_enum._60_40,
      tax_bracket_enum._70_30,
      tax_bracket_enum._80_20,
      tax_bracket_enum._90_10,
      tax_bracket_enum._100_0,
    ],
    [],
  );

  const efficiencyClassOrder = useMemo(
    () => [
      efficiency_class_enum.AAA,
      efficiency_class_enum.AA,
      efficiency_class_enum.A,
      efficiency_class_enum.B,
      efficiency_class_enum.C,
      efficiency_class_enum.D,
      efficiency_class_enum.E,
      efficiency_class_enum.F,
      efficiency_class_enum.G,
      efficiency_class_enum.H,
      efficiency_class_enum.NOT_APPLICABLE,
      efficiency_class_enum.UNKNOWN,
    ],
    [],
  );

  const options: Partial<typeof filters> = useMemo(
    () => ({
      // Previously we were fetching the possible options, but now we are not as it was causing performance issues
      [BuildingFiltersKeys.classOfUses]: Object.values(class_of_use_enum),
      [BuildingFiltersKeys.energyCertificateTypes]: Object.values(energy_certificate_type_enum),
      [BuildingFiltersKeys.typeOfUses]: Object.values(type_of_use_enum),
      [BuildingFiltersKeys.energySourceTypes]: Object.values(energy_source_type_enum),
      [BuildingFiltersKeys.efficiencyClasses]: efficiencyClassOrder,
      [BuildingFiltersKeys.taxBrackets]: taxBracketOrder,
      [BuildingFiltersKeys.euTaxonomyCompliance]: euTaxonomyComplianceOptions,
      [BuildingFiltersKeys.buildingStates]: Object.values(building_state_enum),
      [BuildingFiltersKeys.monumentProtections]: [true, false],
      [BuildingFiltersKeys.heritageDistrict]: [true, false],
      [BuildingFiltersKeys.milieuProtection]: [true, false],
      [BuildingFiltersKeys.leasehold]: [true, false],
      [BuildingFiltersKeys.districtHeatingAvailability]: [true, false],
      [BuildingFiltersKeys.isLod2]: [false, true],
    }),
    [efficiencyClassOrder, taxBracketOrder, euTaxonomyComplianceOptions],
  );

  const getInputFilterByGroup = (group: 'primaryEnergy' | 'finalEnergy' | 'co2Emissions' | 'strandingYear') => {
    switch (group) {
      case 'co2Emissions':
        return (
          <RangeFilter
            minKey={BuildingFiltersKeys.minCo2Emissions}
            maxKey={BuildingFiltersKeys.maxCo2Emissions}
            title={t('General_CO2Intensity')}
            unit={Units.co2EmissionsPerAreaYear}
            minFilter={filters.minCo2Emissions}
            maxFilter={filters.maxCo2Emissions}
          />
        );
      case 'finalEnergy':
        return (
          <RangeFilter
            minKey={BuildingFiltersKeys.minFinalEnergy}
            maxKey={BuildingFiltersKeys.maxFinalEnergy}
            title={t('General_FinalEnergy')}
            unit={Units.energyPerAreaYear}
            minFilter={filters.minFinalEnergy}
            maxFilter={filters.maxFinalEnergy}
          />
        );
      case 'primaryEnergy':
        return (
          <RangeFilter
            minKey={BuildingFiltersKeys.minPrimaryEnergy}
            maxKey={BuildingFiltersKeys.maxPrimaryEnergy}
            title={t('General_PrimaryEnergy')}
            unit={Units.energyPerAreaYear}
            maxFilter={filters.maxPrimaryEnergy}
            minFilter={filters.minPrimaryEnergy}
          />
        );
      case 'strandingYear':
        return (
          <CrremGuard>
            {() => (
              <Stack>
                <RangeFilter
                  isStrandingYearsFilter
                  minKey={BuildingFiltersKeys.minStrandingYear}
                  maxKey={BuildingFiltersKeys.maxStrandingYear}
                  title={strandingYear}
                  minFilter={filters.minStrandingYear}
                  maxFilter={filters.maxStrandingYear}
                />
              </Stack>
            )}
          </CrremGuard>
        );
      default:
        throw new Error(`Unknown group: ${group}`);
    }
  };

  const getFilter = (filter: (typeof BuildingFilters)[number]) => {
    switch (filter.showAs) {
      case 'chip':
        const chipFilter = filter as ChipSearchParamsArray[number];

        return (
          <ChipFilter
            key={chipFilter.key}
            title={chipFilter.title}
            type={chipFilter.key}
            options={options[chipFilter.key] ?? null}
            selectedOptions={filters[chipFilter.key]}
          />
        );
      case 'buildingSystemStatus':
        // "Building system status" is driven by a single active-state enum filter.
        return <BuildingSystemStatusFilter key={filter.key} />;
      case 'radio':
        if (filter.key === BuildingFiltersKeys.slfCompliant) {
          return <SlfCompliantFilter key={filter.key} />;
        }

        return <HasConsumptionDataFilter key={filter.key} />;
      case 'checkbox':
        const checkboxFilter = filter as CheckboxSearchParamsArray[number];

        if (checkboxFilter.key === BuildingFiltersKeys.energyCertificateTypes) {
          return <EnergyCertificateTypeFilter title={checkboxFilter.title} />;
        }

        if (
          checkboxFilter.key === BuildingFiltersKeys.responsibleUserIds ||
          checkboxFilter.key === BuildingFiltersKeys.economicUnitResponsibleUserIds
        ) {
          return (
            <CheckboxFilter
              key={checkboxFilter.key}
              title={
                checkboxFilter.key === BuildingFiltersKeys.responsibleUserIds ? (
                  <Trans i18nKey="General_ResponsibleUser" />
                ) : (
                  <Trans
                    i18nKey="General_EconomicUnitManager"
                    values={{ orgSector: effectiveResponsibleUserOrgSector }}
                  />
                )
              }
              type={checkboxFilter.key}
              options={userOptions.map((u) => u.id)}
              selectedOptions={
                checkboxFilter.key === BuildingFiltersKeys.responsibleUserIds
                  ? filters.responsibleUserIds
                  : filters.economicUnitResponsibleUserIds
              }
              getLabelForItem={(id) => userOptions.find((u) => u.id === id)?.label ?? String(id)}
            />
          );
        }

        return (
          <CheckboxFilter
            key={checkboxFilter.key}
            title={checkboxFilter.title}
            type={checkboxFilter.key}
            options={options[checkboxFilter.key] ?? null}
            selectedOptions={filters[checkboxFilter.key]}
          />
        );
      case 'input':
        const group = filter.group;
        if (!group) {
          throw new Error('Group is required for input filter');
        }

        return getInputFilterByGroup(group);
      default:
        throw new Error(`Unknown filter`);
    }
  };

  const handlePopoverClose = useCallback(() => {
    handleClose();
    resetFilters('local-state-only');
  }, [handleClose, resetFilters]);

  const filterCount = calculateFilterCount(filters);

  return (
    <>
      <MenuPopover
        open={anchorEl}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        sx={{ width: 400, p: 0, mt: 0.5, ml: 0, zIndex: 9999, ...sx }}
        arrow={arrow}
        disabledArrow
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 3, pr: 2, pt: 2 }}>
          <PTypography variant="h6">{t('General_Filter', { count: 2 })}</PTypography>
          <IconButton onClick={handleClose} data-testid="close-filters-more-menu">
            <Iconify icon={ICONS.EVA_CLOSE_FILL} width={20} height={20} />
          </IconButton>
        </Stack>

        <Scrollbar sx={{ maxHeight: 500 }}>
          {BuildingFilters.filter((filter) => !filter.hide && !hiddenFilters?.includes(filter.key)).map((filter) => (
            <Fragment key={filter.key}>{getFilter(filter)}</Fragment>
          ))}
        </Scrollbar>

        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ py: 2, px: 3 }}>
          <Button
            variant="text"
            onClick={handleResetFilters}
            disabled={filterCount === 0}
            sx={{ width: 'min-content', whiteSpace: 'nowrap' }}
          >
            {t('General_Clear')}
          </Button>

          <LoadingButton
            variant="contained"
            onClick={handleApplyFilters}
            loading={isLoading}
            disabled={isLoading || hasValidationError}
            sx={{ width: 'min-content', whiteSpace: 'nowrap' }}
          >
            {t('General_Apply')}
          </LoadingButton>
        </Stack>
      </MenuPopover>
    </>
  );
}
